import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import i18n from 'i18next';
import { Button, DialogActions, TextField, Typography } from '@mui/material';
import { Dialog } from 'components/shared';
import type { RejectReasonDialogProps } from './RejectReasonDialog.types';
import { Content } from './RejectReasonDialog.styles';

/**
 * Low on purpose: a genuine reason can be a single short word ("Fout", "Dubbel"), so anything higher
 * refuses input that is perfectly meaningful. It only has to stop a stray character or two. Kept in
 * sync with HoursPayOutRequestSetStatusDelegate.
 */
const MINIMUM_REASON_LENGTH = 3;

const schema = Yup.object().shape({
  statusNote: Yup.string()
    .trim()
    .required(i18n.t('hoursPayOutRequestsOverview.reasonRequired'))
    .min(
      MINIMUM_REASON_LENGTH,
      i18n.t('hoursPayOutRequestsOverview.reasonTooShort')
    )
});

/**
 * Asks for the reason a request is rejected. The reason is mandatory, and on a bulk reject the same
 * reason is stored on every selected request.
 */
const RejectReasonDialog = ({
  open,
  numberOfRequests,
  onSubmit,
  onCancel
}: RejectReasonDialogProps) => {
  'use memo';

  const { t } = useTranslation('common');

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { statusNote: '' }
  });

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const submit = handleSubmit(({ statusNote }) => {
    reset();
    onSubmit(statusNote);
  });

  const isSingle = numberOfRequests <= 1;

  return (
    <Dialog
      aria-labelledby="hours-pay-out-reject-dialog"
      open={open}
      title={t(
        isSingle
          ? 'hoursPayOutRequestsOverview.rejectTitleSingle'
          : 'hoursPayOutRequestsOverview.rejectTitleMultiple'
      )}
      handleClose={handleCancel}
    >
      <Content>
        <Typography>
          {t(
            isSingle
              ? 'hoursPayOutRequestsOverview.rejectMessageSingle'
              : 'hoursPayOutRequestsOverview.rejectMessageMultiple'
          )}
        </Typography>
        <Controller
          name="statusNote"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              autoFocus
              fullWidth
              variant="outlined"
              multiline
              minRows={3}
              label={t('hoursPayOutRequestsOverview.reason')}
              error={!!errors.statusNote}
              helperText={errors.statusNote?.message}
            />
          )}
        />
        <DialogActions className="dialog-actions">
          <Button onClick={handleCancel} color="secondary" variant="contained">
            {t('cancel')}
          </Button>
          <Button variant="contained" color="primary" onClick={submit}>
            {t('hoursPayOutRequestsOverview.actions.REJECT')}
          </Button>
        </DialogActions>
      </Content>
    </Dialog>
  );
};

export default RejectReasonDialog;
